import { Button, Grid, FormLabel } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { change, Field, FieldArray, getFormValues } from 'redux-form/immutable';

import ProfileIcon from 'assets/icons/profile.svg';
import FormCheckbox from 'retired/FormCheckbox';
import FormNonprofitsSelectorRedesign from 'retired/FormNonprofitsSelectorRedesign';
import Img from 'retired/Img';
import { PrimaryButton } from 'retired/PrimaryButton';
import { selectVolunteerById } from 'entities/volunteers/selectors';
import { lightGray, white } from 'utils/color-styles';

import { EDIT_AFFILIATION_FORM_IDENTIFIER } from './constants';
import { AffiliationBoardPositionSelector } from './AffiliationBoardPositionSelector';
import { AffiliationDatePicker } from './AffiliationDatePicker';
import { AffiliationTypeSelector } from './AffiliationTypeSelector';

const validate = (values: any): any => {
  const formValues = values?.toObject();
  const errors = {};

  if (!formValues?.nonprofits || formValues?.nonprofits?.size === 0) {
    errors.nonprofits = 'Please select a nonprofit';
  }

  if (!formValues?.affiliationType) {
    errors.affiliationType = 'Please select an affiliation';
  }

  if (!formValues?.startingAt) {
    errors.startingAt = 'Please select a valid start date';
  }

  if (!formValues?.endingAt && !formValues?.ongoing) {
    errors.endingAt = 'Please select a valid end date';
  }

  if (!formValues?.boardPosition && formValues?.affiliationType === 'Board Member') {
    errors.boardPosition = 'Please select a board position';
  }
  return errors;
};

export const AffiliationsForm = ({
  initialLocation,
  submit,
  form,
  submitting,
}: {
  initialLocation: string;
  submit: (formValues: Record<string, unknown>) => void;
  form: string;
  submitting: boolean;
}): JSX.Element => {
  const { userId } = useParams<{ userId: string }>();
  const volunteer = useSelector((state) => selectVolunteerById(state, userId));
  const formValues = useSelector(getFormValues(form));
  const dispatch = useDispatch();

  const isAffiliationOngoing = formValues?.get('ongoing');

  useEffect(() => {
    if (isAffiliationOngoing) {
      dispatch(change(form, 'endingAt', null));
    }
  }, [isAffiliationOngoing]);

  const selectedAffiliation = formValues?.get('affiliationType');
  const selectedBoardPosition = formValues?.get('boardPosition');

  useEffect(() => {
    if (selectedBoardPosition && selectedAffiliation !== 'Board Member') {
      dispatch(change(form, 'boardPosition', null));
    }
  }, [selectedAffiliation]);

  const [showErrors, setShowErrors] = useState(false);

  const errors = validate(formValues);

  return (
    <Grid container spacing={10}>
      <Grid item md={8}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setShowErrors(true);
            if (Object.keys(errors).length === 0) {
              submit(formValues);
            }
          }}
        >
          <fieldset>
            <div
              style={{
                paddingTop: 0,
                marginBottom: 30,
                borderRadius: 12,
                border: `1px solid ${lightGray}`,
                padding: 32,
                backgroundColor: white,
              }}
            >
              <div style={{ display: 'flex', marginBottom: 20 }}>
                <div style={{ margin: 20 }}>
                  <Img
                    src={volunteer?.get('mainPicture') || ProfileIcon}
                    alt="Profile picture"
                    style={{
                      height: 50,
                      width: 50,
                      display: 'block',
                      borderRadius: 100,
                    }}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <h1 style={{ fontSize: 16 }}>
                    {volunteer &&
                      `${volunteer?.get('fullName').first} ${
                        volunteer?.get('fullName').last
                      }'s Affiliation`}
                  </h1>
                </div>
              </div>

              <div
                style={{
                  marginTop: 30,
                }}
              >
                <FieldArray
                  name="nonprofits"
                  component={FormNonprofitsSelectorRedesign}
                  limit={1}
                  initialLocation={initialLocation}
                  allowSelection={form !== EDIT_AFFILIATION_FORM_IDENTIFIER}
                  allowDeselection={form !== EDIT_AFFILIATION_FORM_IDENTIFIER}
                />
                {showErrors && errors?.nonprofits && (
                  <FormLabel error style={{ fontSize: 12, marginTop: 5 }}>
                    {errors.nonprofits}
                  </FormLabel>
                )}
              </div>
              <div style={{ marginTop: 30 }}>
                <Field
                  name="affiliationType"
                  component={AffiliationTypeSelector}
                  label="Select Affiliation"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                {showErrors && errors?.affiliationType && (
                  <FormLabel error style={{ fontSize: 12, marginTop: 5 }}>
                    {errors.affiliationType}
                  </FormLabel>
                )}
              </div>

              {formValues?.get('affiliationType') === 'Board Member' && (
                <div style={{ marginTop: 30 }}>
                  <Field
                    name="boardPosition"
                    component={AffiliationBoardPositionSelector}
                    label="Select Board Position"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  {showErrors && errors?.boardPosition && (
                    <FormLabel error style={{ fontSize: 12, marginTop: 5 }}>
                      {errors.boardPosition}
                    </FormLabel>
                  )}
                </div>
              )}
              <div style={{ marginTop: 30 }}>
                <Field
                  name="ongoing"
                  component={FormCheckbox}
                  label="Select if affiliation is ongoing"
                />
              </div>
              <div style={{ marginTop: 30 }}>
                <Grid container spacing={2} style={{ justifyContent: 'space-between' }}>
                  <Grid item xs={6}>
                    <Field
                      name="startingAt"
                      component={AffiliationDatePicker}
                      maxDate={formValues?.get('endingAt')}
                      placeholder="Start date"
                    />
                    {showErrors && errors?.startingAt && (
                      <FormLabel error style={{ fontSize: 12, marginTop: 5 }}>
                        {errors.startingAt}
                      </FormLabel>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      disabled={isAffiliationOngoing}
                      name="endingAt"
                      component={AffiliationDatePicker}
                      placeholder="End date"
                      minDate={formValues?.get('startingAt')}
                    />
                    {showErrors && errors?.endingAt && (
                      <FormLabel error style={{ fontSize: 12, marginTop: 5 }}>
                        {errors.endingAt}
                      </FormLabel>
                    )}
                  </Grid>
                </Grid>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <div
                style={{
                  marginRight: 25,
                }}
              >
                <Button
                  type="reset"
                  disabled={submitting}
                  component={Link}
                  to={`/volunteers/${volunteer?.id}`}
                  style={{
                    width: 200,
                  }}
                >
                  Cancel
                </Button>
              </div>
              <div>
                <PrimaryButton
                  type="submit"
                  variant="contained"
                  style={{
                    width: 200,
                  }}
                >
                  Submit
                </PrimaryButton>
              </div>
            </div>
          </fieldset>
        </form>
      </Grid>
    </Grid>
  );
};

export default AffiliationsForm;
